import { MinAmounts, OrderSide } from "@cede/types";
import Decimal from "decimal.js";

/**
 * Compute the minimum amount in the "from" currency based on min amounts and order side
 *
 * For buy orders: Returns the max of minQuoteAmount or minBaseAmount converted to quote
 * For sell orders: Returns the max of minBaseAmount or minQuoteAmount converted to base
 *
 * @param minAmounts - The minimum amounts for base and quote currencies
 * @param orderSide - The order side (buy or sell)
 * @param price - The price for conversion
 * @returns The minimum amount in the "from" currency as a Decimal
 */
export const computeMinAmountInFromCurrency = (
  minAmounts: MinAmounts,
  orderSide: OrderSide,
  price: string,
): Decimal => {
  const minBaseAmount = new Decimal(minAmounts.minBaseAmount || 0);
  const minQuoteAmount = new Decimal(minAmounts.minQuoteAmount || 0);
  const decimalPrice = new Decimal(price || 0);

  if (decimalPrice.lte(0)) {
    return orderSide === "buy" ? minQuoteAmount : minBaseAmount;
  }

  if (orderSide === "buy") {
    const minBaseInQuote = minBaseAmount.mul(decimalPrice);
    return Decimal.max(minQuoteAmount, minBaseInQuote);
  }

  const minQuoteInBase = minQuoteAmount.div(decimalPrice);
  return Decimal.max(minBaseAmount, minQuoteInBase);
};
